import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { buildMentionMatcher } from '../../config/mentions';

/** Lines shown before the first "Show more", and how many each click adds. */
const LINE_STEP = 10;

/**
 * Message body text: wraps inside the bubble and clamps to LINE_STEP lines,
 * revealing another LINE_STEP per "Show more" click.
 *
 * Clamping is measured, not estimated: the element is line-clamped by CSS and
 * `scrollHeight > clientHeight` tells us lines are still hidden. That keeps the
 * toggle honest for any font size, zoom level or bubble width, and it disappears
 * on its own once the whole message is visible.
 *
 * @param text raw message body; newlines are preserved.
 * @param onAccent set on the sender's accent-filled bubble so the toggle stays
 *   legible against the fill instead of using the accent text token.
 * @param matchMentions a matcher from `buildMentionMatcher`, built ONCE per roster by
 *   the container. Preferred over `mentionNames` in a list: passing names makes this
 *   component re-derive the roster per bubble, which is what the matcher exists to
 *   avoid. Absent, mentions are off — the right default for a 1:1 chat and a secret
 *   group, neither of which has mentions at all.
 * @param mentionNames legacy fallback for single, non-list call sites.
 * @param selfName the viewer's own display name, so a mention OF THE READER reads
 *   differently from a mention of someone else. That distinction is the only
 *   reason to highlight in the stream rather than only in the notification feed.
 */
const MessageText = ({
  text,
  className = '',
  style,
  onAccent = false,
  matchMentions,
  mentionNames,
  selfName = '',
}) => {
  const textRef = useRef(null);
  const [maxLines, setMaxLines] = useState(LINE_STEP);
  const [isClamped, setIsClamped] = useState(false);

  const measure = useCallback(() => {
    const node = textRef.current;
    if (!node) return;
    // 1px slack: sub-pixel line boxes make these differ by a fraction even when
    // nothing is actually hidden.
    setIsClamped(node.scrollHeight - node.clientHeight > 1);
  }, []);

  // A recycled row can render a different message at the same index — the new
  // body starts collapsed rather than inheriting the previous one's expansion.
  useEffect(() => setMaxLines(LINE_STEP), [text]);

  useEffect(() => {
    measure();
  }, [measure, text, maxLines]);

  // The bubble is width-constrained, so a pane resize re-wraps the text without
  // changing the clamped element's own height — observe the parent as well.
  useEffect(() => {
    const node = textRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    if (node.parentElement) observer.observe(node.parentElement);
    return () => observer.disconnect();
  }, [measure]);

  const isExpanded = maxLines > LINE_STEP;

  // A matcher supplied by the container is used as-is; `mentionNames` builds a local
  // one, memoised on the array identity so it survives re-renders that don't change
  // the roster. Either way the roster is prepared once, not once per bubble.
  const localMatcher = useMemo(
    () => (matchMentions ? null : buildMentionMatcher(mentionNames)),
    [matchMentions, mentionNames]
  );
  const matcher = matchMentions ?? localMatcher;

  const segments = useMemo(() => matcher(text), [matcher, text]);
  const self = selfName.trim().toLowerCase();

  return (
    <div className="msg-text-wrap">
      <span
        ref={textRef}
        className={`msg-text ${onAccent ? 'on-accent' : ''} ${className}`}
        style={{ ...style, WebkitLineClamp: maxLines }}
      >
        {segments.map((segment, index) =>
          typeof segment === 'string' ? (
            segment
          ) : (
            <span
              // Index is stable here: the segment list is a pure function of the
              // body text, and a different body remounts the whole span.
              key={`m${index}`}
              className={`msg-mention${
                self && segment.text.toLowerCase() === self ? ' is-me' : ''
              }`}
            >
              @{segment.text}
            </span>
          )
        )}
      </span>

      {(isClamped || isExpanded) && (
        <button
          type="button"
          className={`msg-text-more ${onAccent ? 'is-on-accent' : ''}`}
          onClick={() => setMaxLines(isClamped ? maxLines + LINE_STEP : LINE_STEP)}
        >
          {isClamped ? 'Show more' : 'Show less'}
        </button>
      )}
    </div>
  );
};

/**
 * Memoised: one of these renders per text bubble, and the stream above it re-renders
 * on every inbound message. With a stable `matchMentions` from the container, a bubble
 * whose body has not changed now skips the segment scan and the clamp measurement
 * entirely.
 */
export default memo(MessageText);
